import { useState } from "react";
import {
  services,
  CONTACT_PHONE,
  CONTACT_PHONE_TEL,
  CONTACT_EMAIL,
  aboutPageStats,
  OFFICE_ADDRESSES,
  officeMapsUrl,
  officeMapsEmbedUrl,
} from "../data/constants";
import AnimatedCounter from "../components/AnimatedCounter";
import { submitContactForm } from "../api/cmsApi";

export default function ContactPage() {
  const [formData, setFormData] = useState({
    name: "",
    email: "",
    phone: "",
    service: "",
    message: "",
  });
  const [sent, setSent] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState("");

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError("");
    setSubmitting(true);
    try {
      await submitContactForm({
        name: formData.name,
        email: formData.email,
        phone: formData.phone,
        service: formData.service,
        message: formData.message,
        source: "contact",
        route: "contact",
      });
      setSent(true);
      setFormData({ name: "", email: "", phone: "", service: "", message: "" });
      setTimeout(() => setSent(false), 5000);
    } catch (err) {
      setError(err?.message || "Could not send message. Please try again.");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="contact-page">
      {/* Hero */}
      <section className="contact-page-hero animate-on-scroll">
        <div className="container">
          <div className="row g-4 g-lg-5 align-items-center">
            <div className="col-lg-7">
              <span className="contact-page-label">Let&apos;s Talk</span>
              <h1 className="contact-page-hero-title">
                Start your IT conversation{" "}
                <span className="contact-page-accent">today.</span>
              </h1>
              <p className="contact-page-hero-desc">
                Whether you need a structured assessment, a platform deployment, or
                24×7 managed operations — our certified team is ready to engage.
              </p>
            </div>
            <div className="col-lg-5">
              <div className="contact-page-stats-card">
                {aboutPageStats.map((stat, i) => (
                  <div
                    key={stat.label}
                    className={`contact-page-stat${i % 2 === 0 ? " has-right-border" : ""}${i < 2 ? " has-bottom-border" : ""}`}
                  >
                    <AnimatedCounter
                      value={stat.value}
                      suffix={stat.suffix}
                      className="contact-page-stat-num"
                      as="div"
                    />
                    <span className="contact-page-stat-label">{stat.label}</span>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Form + Sidebar */}
      <section className="contact-page-form-section animate-on-scroll">
        <div className="container">
          <div className="row g-5">
            <div className="col-lg-8">
              <form className="contact-page-form" onSubmit={handleSubmit}>
                {sent && (
                  <div className="contact-page-success">
                    <i className="bi bi-check-circle-fill" aria-hidden="true"></i>
                    Message sent successfully! We&apos;ll get back to you soon.
                  </div>
                )}
                {error ? (
                  <div className="contact-page-success" style={{ background: "#fef2f2", color: "#b91c1c" }}>
                    {error}
                  </div>
                ) : null}

                <div className="contact-page-section-head">
                  <span>Send Us a Message</span>
                  <div className="contact-page-section-line" />
                </div>
                <p className="contact-page-form-desc">
                  Fill out the form and our team will get back to you within 24 hours.
                </p>

                <div className="row g-3">
                  <div className="col-md-6">
                    <label className="contact-page-label-field">Full Name</label>
                    <input
                      className="contact-page-input"
                      placeholder="Your name"
                      value={formData.name}
                      onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                      required
                    />
                  </div>
                  <div className="col-md-6">
                    <label className="contact-page-label-field">Email Address</label>
                    <input
                      type="email"
                      className="contact-page-input"
                      placeholder="you@company.com"
                      value={formData.email}
                      onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                      required
                    />
                  </div>
                  <div className="col-md-6">
                    <label className="contact-page-label-field">Phone Number</label>
                    <input
                      className="contact-page-input"
                      placeholder="+91 xxxxx xxxxx"
                      value={formData.phone}
                      onChange={(e) => setFormData({ ...formData, phone: e.target.value })}
                    />
                  </div>
                  <div className="col-md-6">
                    <label className="contact-page-label-field">Service Interested In</label>
                    <select
                      className="contact-page-input"
                      value={formData.service}
                      onChange={(e) => setFormData({ ...formData, service: e.target.value })}
                    >
                      <option value="">Select a service...</option>
                      {services.map((s) => (
                        <option key={s.title} value={s.title}>
                          {s.title}
                        </option>
                      ))}
                    </select>
                  </div>
                  <div className="col-12">
                    <label className="contact-page-label-field">Message</label>
                    <textarea
                      className="contact-page-textarea"
                      rows={5}
                      placeholder="Tell us about your requirements..."
                      value={formData.message}
                      onChange={(e) => setFormData({ ...formData, message: e.target.value })}
                      required
                      style={{ marginBottom: 0 }}
                    />
                  </div>
                  <div className="col-12">
                    <button type="submit" className="contact-page-submit" disabled={submitting}>
                      <i className="bi bi-send-fill" aria-hidden="true"></i>
                      {submitting ? "Sending..." : "Send Message"}
                    </button>
                  </div>
                </div>
              </form>
            </div>

            <div className="col-lg-4">
              <div className="contact-page-sidebar">
                <div className="contact-page-section-head contact-page-section-head--sidebar">
                  <span>Get in Touch</span>
                </div>
                <p className="contact-page-sidebar-desc">
                  Give us a call or drop by anytime. We will be happy to answer your questions.
                </p>

                <div className="contact-page-info-list">
                  <div className="contact-page-info-item">
                    <div className="contact-page-info-icon">
                      <i className="bi bi-telephone-fill" aria-hidden="true"></i>
                    </div>
                    <div>
                      <span className="contact-page-info-label">Phone</span>
                      <a className="contact-page-info-val" href={`tel:${CONTACT_PHONE_TEL}`}>
                        {CONTACT_PHONE}
                      </a>
                    </div>
                  </div>

                  <div className="contact-page-info-item">
                    <div className="contact-page-info-icon">
                      <i className="bi bi-envelope-fill" aria-hidden="true"></i>
                    </div>
                    <div>
                      <span className="contact-page-info-label">Email</span>
                      <a className="contact-page-info-val" href={`mailto:${CONTACT_EMAIL}`}>
                        {CONTACT_EMAIL}
                      </a>
                    </div>
                  </div>

                  <div className="contact-page-info-item">
                    <div className="contact-page-info-icon">
                      <i className="bi bi-geo-alt-fill" aria-hidden="true"></i>
                    </div>
                    <div>
                      <span className="contact-page-info-label">Offices</span>
                      {OFFICE_ADDRESSES.map((office) => (
                        <div key={office.id} className="mb-3">
                          <strong className="d-block mb-1">{office.label}</strong>
                          <a
                            className="contact-page-info-val contact-page-info-val--address"
                            href={officeMapsUrl(office)}
                            target="_blank"
                            rel="noopener noreferrer"
                          >
                            {office.lines.map((line, i) => (
                              <span key={line}>
                                {line}
                                {i < office.lines.length - 1 ? <br /> : null}
                              </span>
                            ))}
                          </a>
                        </div>
                      ))}
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Google Maps — both offices */}
      <section className="contact-page-maps-section animate-on-scroll">
        <div className="container">
          <div className="contact-page-section-head">
            <span>Find Us</span>
            <div className="contact-page-section-line" />
          </div>
          <p className="contact-page-form-desc">
            Visit our Delhi and Mumbai offices — open in Google Maps for directions.
          </p>
          <div className="row g-4">
            {OFFICE_ADDRESSES.map((office) => (
              <div key={office.id} className="col-lg-6">
                <div className="contact-page-map-card">
                  <div className="contact-page-map-head">
                    <div>
                      <h3>{office.label}</h3>
                      <p>
                        {office.lines.map((line, i) => (
                          <span key={line}>
                            {line}
                            {i < office.lines.length - 1 ? " " : ""}
                          </span>
                        ))}
                      </p>
                    </div>
                    <a
                      className="contact-page-map-link"
                      href={officeMapsUrl(office)}
                      target="_blank"
                      rel="noopener noreferrer"
                    >
                      Open in Maps <i className="bi bi-box-arrow-up-right" aria-hidden="true" />
                    </a>
                  </div>
                  <div className="contact-page-map-frame">
                    <iframe
                      title={`${office.label} map`}
                      src={officeMapsEmbedUrl(office)}
                      loading="lazy"
                      referrerPolicy="no-referrer-when-downgrade"
                      allowFullScreen
                    />
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>
    </div>
  );
}
